/**
 * CommittedOrderTable — the Production Tracker "Committed" tab (BLU-811 Part B).
 *
 * One row per OPEN order that still holds stock at this location
 * (`order_item_locations.quantity > 0` on an `orders.status = 'open'` order —
 * the same open-demand predicate the committed reconcile report uses). It
 * answers "what is my committed figure actually made of", so the unit count is
 * the column that has to be trivially scannable, and every row expands to the
 * per-SKU split that adds up to it.
 *
 * Deliberately NOT a production view: an order can hold stock here and have no
 * live production at this floor at all. Where that is the case the Production
 * status cell says so in words rather than borrowing a status chip from history
 * — dressing a finished/cancelled prod_order as current state is the failure
 * mode this column exists to avoid.
 *
 * Mirrors `TrackerQueueTable` for chrome: same `rounded-md border` + `<Table>`
 * shell, same chevron/expand affordance, same 0.2s AnimatePresence body, same
 * `ExpandedRowPanel` indent, so the three Tracker tables read as one surface.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ClientOrgChip } from '@/components/ClientOrgChip';
import { cn } from '@/lib/utils';
import { formatOrderNumber } from '@threadheads/shared';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { DATE_FORMAT, DATE_TIME_FORMAT, useDateFnsLocale } from '@/lib/format-dates';
import {
  useTrackerClientOrg,
  type TrackerClientOrg,
} from '../hooks/useTrackerClientOrg';
import {
  STATUS_LABEL_KEYS,
  statusColors,
  FULFILLMENT_STATUS_LABEL_KEYS,
  FULFILLMENT_STATUS_BADGE_CLASS,
  NOT_IN_PRODUCTION,
  sortStatusKeysByRank,
} from '../lib/order-status-display';
import { ExpandedRowPanel, ExpandedRowMessage } from './ExpandedOrderContent';
import { TrackerTableSkeleton } from './TrackerTableSkeleton';

// ============================================================================
// TYPES
// ============================================================================

export interface CommittedOrderSku {
  sku: string;
  units: number;
}

/**
 * A row of `productionOrders.listCommittedOrders`. Declared locally rather than
 * inferred off `RouterOutputs` for the same reason `TrackerOrderTable` declares
 * `TrackerOrder`: the table renders from a shape, not from a procedure, and a
 * local interface keeps the render contract readable at the point of use.
 */
export interface CommittedOrder {
  order_id: string;
  order_name: string | null;
  order_number: string | null;
  shopify_order_id: number | null;
  order_date: string;
  organization_id: string | null;
  committed_units: number;
  sku_count: number;
  skus: CommittedOrderSku[];
  display_fulfillment_status: string | null;
  order_status: string | null;
  /** NULL = no LIVE production for this order at this floor. Never a dash. */
  production_status: string | null;
  /** Distinct live statuses at this floor; >1 means the row shows the least advanced. */
  live_status_count: number | null;
  shipped_at: string | null;
}

interface CommittedOrderTableProps {
  orders: CommittedOrder[];
  shopifyDomain?: string | null;
  isLoading?: boolean;
  // BLU-811: summary totals for the column-header hover-tooltips. Order → orderCount,
  // Units → unitCount, SKUs → distinctSkus (a global distinct across the set, NOT the
  // sum of the per-row sku_count), Production status → the statusBreakdown facet. The
  // three numeric totals track the CURRENTLY DISPLAYED (status-filtered) list; the
  // breakdown is the client-scoped facet (all statuses, status-filter-independent).
  // Optional AND undefined-carrying: undefined means the summary is not READY (loading
  // or errored, as well as the isLoading skeleton render) — the header then suppresses
  // the total's tooltip rather than announcing an unvouchable 0.
  orderCount?: number;
  unitCount?: number;
  distinctSkus?: number;
  statusBreakdown?: Record<string, number>;
  /** Hide the totals tooltips: the summary is search-blind, so under a text search its
   *  totals would contradict the filtered rows. */
  searchActive?: boolean;
}

/**
 * Skeleton bar per column, in column order: order · orderDate · [client] ·
 * skus · units · shopify · production. Not exported — the page renders this
 * table with `isLoading` exactly as it does its two siblings, so the widths
 * stay next to the header row they have to line up with.
 */
const COMMITTED_SKELETON_WIDTHS = [
  'w-24', // order
  'w-20', // order date
  'w-10', // skus
  'w-12', // units
  'w-28', // shopify
  'w-24', // production
  'w-20', // client (when shown)
] as const;

// ============================================================================
// EXPANDED CONTENT — the per-SKU split behind the Units figure
// ============================================================================

function ExpandedCommittedContent({ order }: { order: CommittedOrder }) {
  const { t } = useTranslation('production');
  const locale = useDateFnsLocale();

  if (!order.skus || order.skus.length === 0) {
    return <ExpandedRowMessage>{t('tracker.committed.noSkus')}</ExpandedRowMessage>;
  }

  return (
    <ExpandedRowPanel>
      {/* Order date now has its own column, so the expansion instead carries
          shipped_at — null for a typical open committed order, which is why it
          was pulled out of the always-visible columns and shown here only when
          the order has actually shipped. */}
      {order.shipped_at && (
        <p className="pb-2 text-xs text-muted-foreground tabular-nums">
          {t('tracker.committed.expanded.shippedAt', {
            date: format(new Date(order.shipped_at), DATE_TIME_FORMAT, { locale }),
          })}
        </p>
      )}
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-xs h-8 pb-1">
              {t('tracker.committed.expanded.sku')}
            </TableHead>
            <TableHead className="text-xs h-8 pb-1 text-right">
              {t('tracker.committed.expanded.units')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {order.skus.map((line) => (
            <TableRow key={line.sku} className="hover:bg-transparent">
              <TableCell className="py-1.5 font-mono text-xs">{line.sku}</TableCell>
              <TableCell className="py-1.5 text-xs text-right tabular-nums">
                {line.units}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ExpandedRowPanel>
  );
}

// ============================================================================
// ROW + EXPANSION
// ============================================================================

function CommittedRowGroup({
  order,
  isExpanded,
  onToggle,
  shopifyDomain,
  clientOrg,
  showClientColumn,
  colSpan,
}: {
  order: CommittedOrder;
  isExpanded: boolean;
  onToggle: () => void;
  shopifyDomain?: string | null;
  clientOrg: TrackerClientOrg;
  showClientColumn: boolean;
  colSpan: number;
}) {
  const { t } = useTranslation('production');
  const locale = useDateFnsLocale();
  const shouldReduceMotion = useReducedMotion();

  // Same fallback the export uses, so a row and its CSV line name the order
  // identically: order_name when Shopify sent one, else the bare number.
  const displayName = order.order_name ?? (order.order_number ? `#${order.order_number}` : '');
  const shopifyUrl = getShopifyAdminOrderUrl(shopifyDomain, order.shopify_order_id);
  const fulfillmentKey = order.display_fulfillment_status
    ? FULFILLMENT_STATUS_LABEL_KEYS[order.display_fulfillment_status]
    : undefined;
  const productionStatus = order.production_status;
  // >1 distinct live status at this floor: the cell shows the LEAST advanced one
  // (server-ranked), so the suffix says how many others are behind it.
  const otherStatusCount = (order.live_status_count ?? 0) > 1
    ? (order.live_status_count as number) - 1
    : 0;

  return (
    <>
      <TableRow
        className={cn(
          'cursor-pointer hover:bg-muted/50 transition-colors',
          isExpanded && 'bg-muted/30'
        )}
        onClick={onToggle}
        tabIndex={0}
        aria-label={t('tracker.committed.rowAria', {
          order: formatOrderNumber(displayName),
          count: order.committed_units,
        })}
        aria-expanded={isExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <TableCell className="w-10 px-3">
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform motion-reduce:transition-none',
              isExpanded && 'rotate-90'
            )}
            aria-hidden="true"
          />
        </TableCell>

        {/* Order */}
        <TableCell className="py-2.5 px-3 font-medium">
          {shopifyUrl ? (
            <a
              href={shopifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {formatOrderNumber(displayName)}
            </a>
          ) : (
            <span>{formatOrderNumber(displayName)}</span>
          )}
        </TableCell>

        {/* Order date — the sort key, always populated. Given its own column so
            the sort control reorders rows against something the operator can
            actually see (shipped_at moved to the expansion in its place). */}
        <TableCell className="py-2.5 px-3 text-xs text-muted-foreground tabular-nums whitespace-nowrap">
          {format(new Date(order.order_date), DATE_FORMAT, { locale })}
        </TableCell>

        {/* Client — the column only exists when some row is cross-org (DESIGN
            §13.2: a badge on every row of a single-tenant list is noise). */}
        {showClientColumn && (
          <TableCell className="py-2.5 px-3">
            {clientOrg.isCrossOrg(order.organization_id) ? (
              <ClientOrgChip label={clientOrg.labelFor(order.organization_id)} />
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </TableCell>
        )}

        {/* SKUs — what the expansion contains, so it doubles as the affordance. */}
        <TableCell className="py-2.5 px-3 text-muted-foreground tabular-nums">
          {order.sku_count}
        </TableCell>

        {/* Units — the figure the whole tab exists to explain. */}
        <TableCell className="py-2.5 px-3 text-right font-medium tabular-nums">
          {order.committed_units}
        </TableCell>

        {/* Shopify fulfillment */}
        <TableCell className="py-2.5 px-3">
          {order.display_fulfillment_status ? (
            <Badge variant="outline" className={FULFILLMENT_STATUS_BADGE_CLASS}>
              {fulfillmentKey
                ? t(fulfillmentKey)
                : order.display_fulfillment_status.replace(/_/g, ' ')}
            </Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>

        {/* Production status — words, not a dash, when there is no live part
            here: "—" would read as missing data rather than "none exists". */}
        <TableCell className="py-2.5 px-3">
          {productionStatus ? (
            <div className="flex items-center gap-1.5">
              <Badge
                variant="secondary"
                className={statusColors[productionStatus] ?? statusColors.NEW}
              >
                {STATUS_LABEL_KEYS[productionStatus]
                  ? t(STATUS_LABEL_KEYS[productionStatus])
                  : productionStatus.replace(/_/g, ' ')}
              </Badge>
              {otherStatusCount > 0 && (
                <>
                  {/* aria-label on a bare span is unreliably announced; the
                      visible glyph is hidden and the honest count is given to
                      assistive tech via sr-only text instead. */}
                  <span
                    className="text-xs text-muted-foreground tabular-nums"
                    aria-hidden="true"
                  >
                    +{otherStatusCount}
                  </span>
                  <span className="sr-only">
                    {t('tracker.committed.moreStatuses', { count: otherStatusCount })}
                  </span>
                </>
              )}
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">
              {t('tracker.committed.notInProduction')}
            </span>
          )}
        </TableCell>
      </TableRow>

      <AnimatePresence>
        {isExpanded && (
          <tr>
            <td colSpan={colSpan} className="p-0">
              <motion.div
                initial={shouldReduceMotion ? {} : { opacity: 0, height: 0 }}
                animate={shouldReduceMotion ? {} : { opacity: 1, height: 'auto' }}
                exit={shouldReduceMotion ? {} : { opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden bg-muted/10 border-b"
              >
                <ExpandedCommittedContent order={order} />
              </motion.div>
            </td>
          </tr>
        )}
      </AnimatePresence>
    </>
  );
}

// ============================================================================
// HEADER TOTALS (BLU-811)
// ============================================================================

/**
 * Focusable trigger shared by every header total. `asChild` on the tooltip means the
 * span IS the trigger, so the dotted underline hints "hover/focus for more", the
 * `tabIndex` puts it in the keyboard path, and the focus ring makes that reachable
 * state visible.
 */
const HEADER_TRIGGER_CLASS =
  'cursor-default underline decoration-dotted decoration-muted-foreground/50 underline-offset-4 ' +
  'rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/**
 * A column header whose running total is revealed on hover/focus (Tooltip) AND always
 * exposed to assistive tech via an sr-only span — never information-only (DESIGN §10):
 * the same totals live in the visible summary line above the table, so this is
 * reinforcement, not the sole surface. Hidden entirely while a text search is active,
 * because the summary that feeds it is search-blind.
 */
function TotalHead({
  label,
  detail,
  searchActive,
  className,
}: {
  label: string;
  detail: string;
  searchActive: boolean;
  className?: string;
}) {
  if (searchActive) {
    return <TableHead className={className}>{label}</TableHead>;
  }
  return (
    <TableHead className={className}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className={HEADER_TRIGGER_CLASS}>
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {detail}
        </TooltipContent>
      </Tooltip>
      <span className="sr-only">{detail}</span>
    </TableHead>
  );
}

/**
 * The Production-status header total: a per-status breakdown rather than a single
 * figure. Rows are ordered by the shared STATUS_RANK (same order as the filter) and
 * each carries its status colour — EXCEPT NOT_IN_PRODUCTION, which renders muted and
 * badge-less so it is never dressed as a live production state (it must not fall
 * through to NEW's slate chip). The breakdown is the client-scoped facet: it lists all
 * statuses regardless of the active filter, so it also shows what else you could
 * filter to.
 */
function ProductionStatusHead({
  label,
  breakdown,
  searchActive,
}: {
  label: string;
  breakdown: Record<string, number>;
  searchActive: boolean;
}) {
  const { t } = useTranslation('production');

  const labelForStatus = (status: string) => {
    const key =
      status === NOT_IN_PRODUCTION
        ? 'tracker.committed.notInProduction'
        : STATUS_LABEL_KEYS[status];
    return key ? t(key) : status.replace(/_/g, ' ');
  };

  const entries = sortStatusKeysByRank(Object.keys(breakdown)).map((status) => ({
    status,
    count: breakdown[status] ?? 0,
  }));

  if (searchActive || entries.length === 0) {
    return <TableHead>{label}</TableHead>;
  }

  const sep = t('tracker.committed.header.tooltip.listSeparator');
  const srText =
    t('tracker.committed.header.tooltip.production') +
    sep +
    entries
      .map((e) =>
        t('tracker.committed.header.tooltip.breakdownRow', {
          label: labelForStatus(e.status),
          count: e.count,
        }),
      )
      .join(sep);

  return (
    <TableHead>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className={HEADER_TRIGGER_CLASS}>
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <p className="mb-1.5 font-medium text-foreground">
            {t('tracker.committed.header.tooltip.production')}
          </p>
          <ul className="space-y-1">
            {entries.map((e) => (
              <li
                key={e.status}
                className="flex items-center justify-between gap-3"
              >
                {e.status === NOT_IN_PRODUCTION ? (
                  <span className="text-muted-foreground">{labelForStatus(e.status)}</span>
                ) : (
                  <Badge
                    variant="secondary"
                    className={statusColors[e.status] ?? statusColors.NEW}
                  >
                    {labelForStatus(e.status)}
                  </Badge>
                )}
                <span className="tabular-nums text-muted-foreground">{e.count}</span>
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
      <span className="sr-only">{srText}</span>
    </TableHead>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function CommittedOrderTable({
  orders,
  shopifyDomain,
  isLoading,
  orderCount,
  unitCount,
  distinctSkus,
  statusBreakdown,
  searchActive = false,
}: CommittedOrderTableProps) {
  // Resolved once here, not per row — one query subscription instead of one per
  // rendered order (same reason as the two sibling tables).
  const clientOrg = useTrackerClientOrg();
  const { t } = useTranslation('production');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  // The numeric header totals come from the summary query. When it is loading or
  // errored the page passes them as undefined; a "0" tooltip beside real rows would be
  // an unvouchable reconciliation figure. Treat missing totals exactly like an active
  // search — render the bare header with no tooltip. The Production-status breakdown is
  // a SEPARATE data source (the base summary) and stays gated on `searchActive` alone:
  // it already degrades to a bare header on an empty facet (see ProductionStatusHead).
  const totalsReady =
    orderCount !== undefined && unitCount !== undefined && distinctSkus !== undefined;
  const suppressTotals = searchActive || !totalsReady;

  const toggleExpand = (orderId: string) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  if (isLoading) {
    return <TrackerTableSkeleton widths={COMMITTED_SKELETON_WIDTHS} />;
  }

  if (orders.length === 0) {
    return null; // Parent handles empty state
  }

  const showClientColumn = orders.some((order) => clientOrg.isCrossOrg(order.organization_id));
  // chevron + order + orderDate + skus + units + shopify + production (+ client)
  const colSpan = showClientColumn ? 8 : 7;

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <span className="sr-only">{t('tracker.committed.header.expand')}</span>
            </TableHead>
            <TotalHead
              label={t('tracker.committed.header.order')}
              detail={t('tracker.committed.header.tooltip.orders', { count: orderCount ?? 0 })}
              searchActive={suppressTotals}
            />
            <TableHead>{t('tracker.committed.header.orderDate')}</TableHead>
            {showClientColumn && <TableHead>{t('tracker.committed.header.client')}</TableHead>}
            <TotalHead
              label={t('tracker.committed.header.skus')}
              detail={t('tracker.committed.header.tooltip.skus', { count: distinctSkus ?? 0 })}
              searchActive={suppressTotals}
            />
            <TotalHead
              label={t('tracker.committed.header.units')}
              detail={t('tracker.committed.header.tooltip.units', { count: unitCount ?? 0 })}
              searchActive={suppressTotals}
              className="text-right"
            />
            <TableHead>{t('tracker.committed.header.shopify')}</TableHead>
            <ProductionStatusHead
              label={t('tracker.committed.header.production')}
              breakdown={statusBreakdown ?? {}}
              searchActive={searchActive}
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order) => (
            <CommittedRowGroup
              key={order.order_id}
              order={order}
              isExpanded={expandedOrderId === order.order_id}
              onToggle={() => toggleExpand(order.order_id)}
              shopifyDomain={shopifyDomain}
              clientOrg={clientOrg}
              showClientColumn={showClientColumn}
              colSpan={colSpan}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
